import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AssetDto, CommentDto } from '@picflow/types';
import { BehaviorSubject, Observable } from 'rxjs';
import { AssetService } from './asset.service';
import { SocketService } from './socket.service';

type AssetUpdateEvent = { asset: AssetDto; name: 'asset.updated' | 'asset.created' | 'asset.deleted' };
type CommentUpdateEvent = { name: 'comment.updated' | 'comment.created' | 'comment.deleted'; comment: CommentDto };

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private _asset = new BehaviorSubject<AssetUpdateEvent>(undefined);
  public readonly onAssetUpdate: Observable<AssetUpdateEvent> = this._asset.asObservable();
  private _comment = new BehaviorSubject<CommentUpdateEvent>(undefined);
  public readonly onCommentUpdate: Observable<CommentUpdateEvent> = this._comment.asObservable();
  private subscriptions: Map<string, { id: string; type: string }> = new Map();
  initted = false;

  constructor(
    private socketService: SocketService,
    private assetService: AssetService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {}

  initSocket() {
    if (isPlatformServer(this.platformId)) return;
    if (this.initted) return;
    this.initted = true;
    this.socketService.onSocketEvent.subscribe(e => {
      if (!e?.payload) return;
      if (e.payload.asset) {
        this.assetService.removeAssets();
        const name = e.name as 'asset.updated' | 'asset.created' | 'asset.deleted';
        const asset = e.payload.asset as AssetDto;
        this._asset.next({ name, asset });
      }
      if (e.payload.comment) {
        const name = e.name as 'comment.updated' | 'comment.created' | 'comment.deleted';
        const comment = e.payload.comment as CommentDto;
        this._comment.next({ name, comment });
      }
    });

    this.socketService.onSocketConnected.subscribe(() => {
      const entries = Array.from(this.subscriptions.entries());
      entries.forEach(([id, { type }]) => {
        this.socketService.sendMessage(type + '.join', this.createParams(id, type));
      });
    });
  }

  public subscribeTenant(id: string) {
    this.subscribe('tenant', id);
  }

  public subscribeGallery(id: string) {
    this.subscribe('gallery', id);
  }

  public subscribeAsset(id: string) {
    this.subscribe('asset', id);
  }

  public unsubscribeGallery(id: string) {
    this.unsubscribe('gallery', id);
  }

  public unsubscribeAsset(id: string) {
    this.unsubscribe('asset', id);
  }

  private async subscribe(type: string, id: string) {
    if (isPlatformServer(this.platformId)) return;
    this.initSocket();
    if (this.subscriptions.has(id)) return;
    await this.socketService.sendMessage(type + '.join', this.createParams(id, type));
    this.subscriptions.set(id, { type, id });
  }

  private unsubscribe(type: string, id: string) {
    if (isPlatformServer(this.platformId)) return;
    this.socketService.sendMessage(type + '.leave', this.createParams(id, type));
    this.subscriptions.delete(id);
  }

  private createParams(id: string, type: string) {
    const params = {};
    params['id_' + type] = id;
    return params;
  }
}
